

import React, { useCallback, useContext, useEffect, useState, useRef } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import moment from 'moment';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import "./taskComponents.scss"
import { MenuItem, Select, FormControl, InputLabel, CircularProgress, Backdrop, Grid, Typography, Autocomplete, ClickAwayListener, Tooltip, Box } from '@mui/material';
import { shallowEqual, useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { addComponent, createJiraTask, getJiraEpics, getJiraProjectComponents, getJiraUser, getPermissions } from '../../services/jira.service';
import { dropDownMenuProps } from '../../utils/components';
import { toast } from 'react-toastify';
import { JIRA_SUBTASK_ISSUE_ID, JIRA_TASK_ISSUE_ID, TASK_CATEGORIES, } from '../../services/const';
import { ResponsibleTeamContext } from '../../Pages/IssuePage';
import InfoIcon from '@mui/icons-material/Info';
import _ from 'lodash';
import DialogActions from "@mui/material/DialogActions";
import TinyMCEEditor from "../tinymce";
import { convertHTMLToADF } from '../../utils/helper';
import { Editor } from 'tinymce';

interface DialogProps {
  isSubTask?: boolean;
  isOpen: boolean;
  onClose: (isRefetch?: boolean, metaData?: any) => void;
  blockParent?: boolean;
}

const createSubtaskInfoTooltipMessage = (
  <div>
    <span>Here you create a sub-task for the selected task or sub-task's parent. Provide the necessary details and comments to describe the new sub-task.</span>
  </div>
);
const createTaskInfoTooltipMessage = (
  <div>
    <span>Here you create a new task. Use this to initiate a new task within the selected project.</span>
  </div>
);

const CreateJiraTaskDialog: React.FC<DialogProps> = ({ isOpen, onClose, blockParent = false, isSubTask = false }) => {

  const userState = useSelector((state: RootState) => state.user, shallowEqual);
  const userDetails = userState?.user;

  const responsibleTeam = useContext(ResponsibleTeamContext);
  const [responsibleTeamList, setResponsibleTeamList] = useState<any[]>([]);
  const [componentsList, setComponentsList] = useState<any[]>([{ name: "testing-components", id: "testing" }]);
  const [componentSearchValue, setComponentSearchValue] = useState<string>("");
  const [componentLoading, setComponentLoading] = useState(false);
  const [selectedComponent, setSelectedComponent] = useState<any>();

  const [epicList, setEpicList] = useState<any[]>([{ name: "testing-epic", id: "testing" }]);
  const [epicLoading, setEpicLoading] = useState(false);
  const [epicSearchValue, setEpicSearchValue] = useState<string>("");
  const [selectedEpic, setSelectedEpic] = useState<any>();

  const [fullScreenLoading, setFullScreenLoading] = useState(false);
  const [epicAbortController, setEpicAbortController] = useState(new AbortController());
  const issueDetails = useSelector((state: RootState) => state.issue, shallowEqual);

  const projectState = useSelector((state: RootState) => state.project);
  const selectedProject = projectState?.selectedProject;
  const projectTypeMenu = projectState.projectTypeMenu;
  const projectType = projectState.projectType;

  const getCurrentDate = () => moment().format('YYYY-MM-DD');
  const [isInfoToolTipOpen, setIsInfoToolTipOpen] = useState(false);
  const [assigneeList, setAssigneeList] = useState<any>([]);
  const [userLoading, setUserLoading] = useState(false);
  const [selectedAssigneeId, setSelectedAssigneeId] = useState("");
  const editorRef = useRef<Editor | null>(null);

  const hasSearchedEpicRef = useRef(true);
  const [componentAssigneeList, setComponentAssigneeList] = useState<any>([]);
  const [componentCreationEnabled, setComponentCreationEnabled] = useState(false);
  const [newComponentDetails, setNewComponentDetails] = useState({
    componentName: "",
    leadAccountId: "",
    leadDisplayName: "",
    isAccess: false
  })
  const initialValues = {
    // commanded for reference
    // {
    //   "fields": {
    //       "assignee": {
    //           "id": "712020:ce7e5ee9-199a-42aa-b5cb-c6ce9f322294"
    //       },
    //       "components": [
    //           {
    //               "id": "10773"
    //           }
    //       ],
    //       "customfield_11241": {
    //           "id": "10529"
    //       },
    //       "customfield_10009": "ALW-13836",
    //       "duedate": "2024-03-12",
    //       "issuetype": {
    //           "id": "11067"
    //       },
    //       "project": {
    //           "id": "13691"
    //       },
    //       "reporter": {
    //           "id": "712020:ce7e5ee9-199a-42aa-b5cb-c6ce9f322294"
    //       },
    //       "summary": "[Testing] Create Issue API Jira Management"
    //   },

    // "assignee" --> "name": "Assignee",
    // "components" --> "name": "Components",
    // "customfield_11241" --> "name": "Responsible Team",
    // "customfield_10009" --> "name": "Epic Link",
    // "customfield_11242" --> "name": "Epic",
    // "issuetype" --> "name": "Issue Type",
    // "project" --> "name": "Project",
    // "reporter" --> "name": "Reporter",
    // "summary" --> "name": "Summary",
    // "description" --> "name": "Description",
    person: "",
    taskName: "",
    components: "",
    customfield_11241: "",
    description: {},
    customfield_10009: "",
    dueDate: getCurrentDate(),
    timeEstimation: {
      days: "",
      hours: "",
      minutes: ""
    },
  };

  const getValidationSchema = () => {
    if (isSubTask) {
      return Yup.object({
        person: Yup.string().required('Assignee is required'),
        taskName: Yup.string().required('Task Name is required'),
        components: Yup.string().required('Component is required'),
        customfield_11241: Yup.string().required('Responsible Team is required'),
        description: Yup.object().test(
          'is-not-empty',
          'Description is required',
          function (value: any) {
            return !!(value && value.content && value.content.length > 0);
          }
        ).required('Description is required'),
        dueDate: Yup.date().required('Due Date is required'),
        timeEstimation: Yup.object({
          hours: Yup.number(),
          minutes: Yup.number(),
        }).required('Time Estimation is required'),
      });
    } else {
      return Yup.object({
        person: Yup.string().required('Assignee is required'),
        customfield_10009: Yup.string().required('Epic is required'),
        taskName: Yup.string().required('Task Name is required'),
        components: Yup.string().required('Component is required'),
        customfield_11241: Yup.string().required('Responsible Team is required'),
        description: Yup.object().test(
          'is-not-empty',
          'Description is required',
          function (value: any) {
            return !!(value && value.content && value.content.length > 0);
          }
        ).required('Description is required'),
        dueDate: Yup.date().required('Due Date is required'),
        timeEstimation: Yup.object({
          hours: Yup.number(),
          minutes: Yup.number(),
        }).required('Time Estimation is required'),
      });
    }
  }
  const validationSchema = getValidationSchema();

  const createJiraTaskForProject = async (values: typeof initialValues) => {
    if (!selectedProject?.projectKey && !issueDetails?.issueData?.fields?.project?.id) {
      return toast.warning('Project/Issue is not selected');
    }

    const payload = {
      blockParent,
      fields: {
        assignee: {
          id: selectedAssigneeId
        },
        estimationTime: values?.timeEstimation,
        components: [
          {
            id: values?.components
          }
        ],
        customfield_11241: {
          id: values?.customfield_11241
        },
        customfield_10009: values?.customfield_10009 || '',
        issuetype: {
          id: JIRA_TASK_ISSUE_ID
        },
        project: {
          id: selectedProject?.id || issueDetails?.issueData?.fields?.project?.id
        },
        duedate: values?.dueDate,
        summary: values?.taskName,
        description: values?.description
      },
    }
    setFullScreenLoading(true);
    try {
      let response = await createJiraTask(payload);
      const reponseData = response?.data;
      if (response?.statusCode === 200 && reponseData?.id) {
        toast.success(response?.message || 'Task created successfully');
        formik.resetForm();
        onClose(true, response?.data);
        return;
      }
      toast.error('Task created failed');
    } catch {
      toast.error('Task created failed');
      setFullScreenLoading(false);
    } finally {
      setFullScreenLoading(false);
    }
  }

  const extractParentData = () => {
    if (issueDetails?.issueType === "Subtask_") {
      if (issueDetails?.issueData?.fields?.parent) {
        return {
          ...issueDetails.issueData.fields.parent,
          issue: issueDetails.issueData.fields.parent?.fields?.summary,
        };
      }
    }
    return issueDetails;
  };

  const parentData = extractParentData();

  const createJiraSubTask = async (values: typeof initialValues) => {
    if (!parentData?.id) {
      return toast.warning('Task not selected');
    }

    const payload = {
      blockParent,
      parentKey: parentData?.key,
      fields: {
        assignee: {
          id: selectedAssigneeId
        },
        estimationTime: values?.timeEstimation,
        components: [
          {
            id: values?.components
          }
        ],
        customfield_11241: {
          id: values?.customfield_11241
        },
        issuetype: {
          id: JIRA_SUBTASK_ISSUE_ID
        },
        parent: {
          id: parentData?.id
        },
        project: {
          id:  selectedProject?.id || issueDetails?.issueData?.fields?.project?.id
        },
        duedate: values?.dueDate,
        summary: values?.taskName,
        description: values?.description
      },
    }
    setFullScreenLoading(true);
    try {
      let response = await createJiraTask(payload);
      const responseData = response?.data;
      if (response?.statusCode === 200 && responseData?.id) {
        toast.success(response?.message || 'Sub-task created successfully');
        formik.resetForm();
        onClose(true, response?.data);
        return;
      }
      toast.error('Sub-task created failled');
    } catch {
      toast.error('Sub-task created failled');
    } finally {
      setFullScreenLoading(false);
    }
  }

  const onSubmit = async (values: typeof initialValues) => {
    if (isSubTask) {
      createJiraSubTask(values);
    } else {
      createJiraTaskForProject(values);
    }
  };

  const handleClose = () => {
    formik.resetForm();
    onComponentEnableClose()
    onClose(false);
  }


  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit
  });

  const debounce = <F extends (...args: any[]) => void>(func: F, delay: number) => {
    let timer: NodeJS.Timeout;
    return function (this: any, ...args: any[]) {
      clearTimeout(timer);
      timer = setTimeout(() => func.apply(this, args), delay);
    } as F;
  };

  const getEpicList = async (searchText?: string, abortController?: AbortController, isFirstTime:boolean = true) => {
    let epicData: any = [];
    let isCanceled: boolean = false;
    setEpicLoading(true);

    try {
      if (selectedProject?.projectKey) {

        const epicDataResponse = await getJiraEpics({ projectKey: selectedProject?.projectKey, searchText }, abortController?.signal);
        if (epicDataResponse?.message === "canceled") {
          isCanceled = true;
        }
        epicData = epicDataResponse?.data?.issues;
        if(epicDataResponse?.data?.issues && issueDetails.issueType === "Epic" && issueDetails.key && isFirstTime){
          const epicPreSelected = epicDataResponse?.data?.issues.find((epicIssue: any) => epicIssue?.key === issueDetails.key );
          if(epicPreSelected){ 
            setSelectedEpic(epicPreSelected)
            formik.setFieldValue("customfield_10009", epicPreSelected?.key);
            setEpicSearchValue(epicPreSelected?.fields?.summary || "");
          }
        }
      }
    } catch (error) {
      epicData = [];
    } finally {
      if (isCanceled) return;
      setEpicLoading(false);
      const newEpicData = Array.isArray(epicData) ? epicData : [];
      setEpicList(newEpicData);
    }
  }


  const debouncedEpicSearch = debounce((value: string, controller: AbortController) => {
    getEpicList(value, controller, !hasSearchedEpicRef.current);
    hasSearchedEpicRef.current = true;
  }, 500);

  const handleEpicSearch = async (value: any, reason: any) => {
    if (reason === "input") {
      setEpicSearchValue(value);
      if (epicAbortController) {
        epicAbortController.abort();
      }
      const newEpicAbortController = new AbortController();
      setEpicAbortController(newEpicAbortController);
      debouncedEpicSearch(value, newEpicAbortController);
    }
  }

  const handleEpicChange = (event: any, value: any, reason: any) => {
    if (reason === "selectOption") {
      setSelectedEpic(value);
      formik.setFieldValue("customfield_10009", value?.key);
      setEpicSearchValue(value?.fields?.summary || "");
    }
  }

  const handleEpicBlur = (value: any) => {
    setEpicSearchValue(selectedEpic?.fields?.summary || "");
    if (!selectedEpic?.fields?.summary) {
      handleEpicSearch("", "input");
    }
  }

  const handleComponentSearch = async (value: any, reason: any) => {
    if (reason === "input") {
      setComponentSearchValue(value);
    }
  }

  const handleComponentChange = (event: any, value: any, reason: any) => {
    if (reason === "selectOption") {
      setSelectedComponent(value);
      formik.setFieldValue("components", value?.id);
      setComponentSearchValue(value?.name || "");
    }
  }

  const handleComponentBlur = (value: any) => {
    setComponentSearchValue(selectedComponent?.name || "");
  }


  const getProjectComponentsList = async () => {
    let componentsListData = [];
    setComponentLoading(true);
    try {
      if (selectedProject?.projectKey || (issueDetails && issueDetails?.issueData?.fields?.project?.key)) {
        let projectKey = selectedProject?.projectKey || issueDetails?.issueData?.fields?.project?.key || '';
        const componentsData = await getJiraProjectComponents(projectKey);
        if (Array.isArray(componentsData?.data)) {
          componentsListData = componentsData?.data;
        }
      } else {
        componentsListData = [];
      }
    } catch (error) {
      componentsListData = [];
    }
    setComponentLoading(false);
    setComponentsList(componentsListData);
  };

  useEffect(() => {
    const abortController: AbortController = new AbortController();
    if ((projectType === projectTypeMenu[2]) && isOpen) {
      getProjectComponentsList();
      getEpicList(undefined, abortController, true);
    } else {
      setEpicList([]);
      setComponentsList([]);
    }
    return () => {
      abortController.abort()
    }
  }, [selectedProject, isOpen]);


  useEffect(() => {
    if (projectType === projectTypeMenu[2]) {
      // getResponsibleTeamList();
    } else {
      setEpicList([]);
      // setResponsibleTeamList([]);
      setComponentsList([]);
    }
  }, [projectType]);

  useEffect(() => {
    if (isOpen) {
      setEpicSearchValue("");
      setSelectedEpic({});
      setComponentSearchValue("");
      setSelectedComponent({});

      getJiraUserList({ size: 20 });
      getJiraUserListForComponent({ size: 20 })
      getJIRApermissions()
    }
  }, [isOpen])

  useEffect(() => {
    setResponsibleTeamList([...responsibleTeam]);
  }, [responsibleTeam]);

  const [isEpicDownOpen, setIsEpicDownOpen] = useState(false);

  const getJiraUserList = async (params: any) => {
    setUserLoading(true);
    let userData: any = [];
    try {
      const userDataResponse = await getJiraUser(params);
      userData = userDataResponse?.data;
    } catch (error) {
      userData = [];
    } finally {
      setUserLoading(false);
    }
    setAssigneeList([...userData]);
    return userData;
  };

  const getJiraUserListForComponent = async (params: any) => {
    setUserLoading(true);
    let userData: any = [];
    try {
      const userDataResponse = await getJiraUser(params);
      userData = userDataResponse?.data;
    } catch (error) {
      userData = [];
    } finally {
      setUserLoading(false);
    }
    setComponentAssigneeList([...userData]);
    return userData;
  };

  const getJIRApermissions = async () => {
    try{
      const response = await getPermissions(selectedProject?.name || "", "ADMINISTER_PROJECTS");
      if(response.permissions.ADMINISTER_PROJECTS){
        setNewComponentDetails((prev) => ({...prev, isAccess: response.permissions.ADMINISTER_PROJECTS.havePermission || false }) )
      }
    }catch(error: any) {
      setNewComponentDetails((prev) => ({...prev, isAccess: false }) )
    }
  }


  const debouncedSearch = useCallback(
    _.debounce(async () => {
      getJiraUserList({ size: 20, text: formik.values.person });
    }, 2000),
    [getJiraUserList]
  );

  const debounceComponentLeadSerach = useCallback(
    _.debounce(async () => {
      getJiraUserListForComponent({ size: 20, text: newComponentDetails.leadDisplayName });
  }, 2000),[getJiraUserListForComponent, newComponentDetails.leadDisplayName]);

  useEffect(() => {
    setUserLoading(true);
    setAssigneeList([]);
    debouncedSearch();
    return () => {
      debouncedSearch.cancel();
    }
  }, [formik.values.person])

  useEffect(() => {
    setUserLoading(true);
    setAssigneeList([]);
    debounceComponentLeadSerach();
    return () => {
      debounceComponentLeadSerach.cancel()
    }
  },[newComponentDetails.leadDisplayName]);
  
  const getDefaultValue = () => {
    if (blockParent)
      return null

    formik.setFieldValue("person", userDetails?.jiraDetails?.displayName);
    setSelectedAssigneeId(userDetails?.jiraDetails?.accountId || '');
    setNewComponentDetails((prev) => ({...prev, leadAccountId: userDetails?.jiraDetails?.accountId || "", leadDisplayName: userDetails?.jiraDetails?.displayName || ""}))
    return {
      displayName: userDetails?.jiraDetails?.displayName
    };
  }

  const onComponentEnableClose = () => {
    setComponentCreationEnabled(false)
    setNewComponentDetails((prev) =>({
      ...prev,
      leadAccountId: "",
      leadDisplayName: "",
      componentName: '',
    }))
  }

  const handleCreationComponent = async () => {
    try{
      if(!selectedProject?.projectKey || !newComponentDetails.componentName || !newComponentDetails.leadAccountId){
        toast.error("please pass required fields")
        return 
      }

      const response = await addComponent({ projectKey: selectedProject?.projectKey, name: newComponentDetails.componentName, leadAccountId: newComponentDetails.leadAccountId,  projectName: selectedProject.name});
      if(response.statusCode === 200){
        getProjectComponentsList()
      }else{
        throw Error(response)
      }
    } catch (error){
      toast.error("Unable to create component for this user")
    }
  }

  const getDefaultEpicValue = () => {
    if (issueDetails?.key) {
      formik.setFieldValue("customfield_10009", issueDetails.key);
    }
    return {
      label: issueDetails?.issueData?.fields.summary
    }
  }
  return (
    <Dialog
      PaperProps={{
        className: 'create-task-dialog-container',
        sx: {
          width: '800px',
          maxWidth: '90vw',
          minWidth: '300px',
          margin: 'auto', 
        },
      }}
      open={isOpen}
      disableEnforceFocus
    >
      <DialogTitle>
        <div className='title-container'>
          <span>Create {isSubTask ? 'Sub' : ''} Task</span>

          <ClickAwayListener onClickAway={(e) => setIsInfoToolTipOpen(false)}>
            <div>
              <Tooltip
                title={isSubTask ? createSubtaskInfoTooltipMessage : createTaskInfoTooltipMessage}
                PopperProps={{
                  disablePortal: true,
                }}
                onClose={(e) => setIsInfoToolTipOpen(false)}
                open={isInfoToolTipOpen}
                disableFocusListener
                disableHoverListener
                disableTouchListener
              >
                <Button className='info-button' onClick={(e) => setIsInfoToolTipOpen(true)}><InfoIcon className='info-icon' /></Button>
              </Tooltip>
            </div>
          </ClickAwayListener>
        </div>
      </DialogTitle>
      <div className='create-sub-task-dialog-content' >
        <form onSubmit={formik.handleSubmit} className="dialog-form">
          <DialogContent className="create-sub-task-dialog-content"  >
            {
              !!isSubTask && !!parentData?.id && (
                <TextField
                  label="Selected Task"
                  value={`${parentData?.key || ''} ${parentData?.issue || ''}`}
                  disabled
                  fullWidth
                  margin="normal"
                />
              )
            }

            <div className='create-sub-task-form-fields'>
              <Autocomplete
                id="select-assignee"
                options={assigneeList}
                loading={userLoading}
                autoHighlight
                defaultValue={getDefaultValue}
                onChange={(event, value: any) => {
                  const selectedAccountId = value?.accountId || '';
                  setSelectedAssigneeId(selectedAccountId);
                  formik.setValues({
                    ...formik.values,
                    person: value?.displayName || ''
                  })
                }}
                isOptionEqualToValue={(option, value) => option.accountId === value.accountId}
                getOptionLabel={(option: any) => option.displayName}
                renderOption={(props, option) => (
                  <Box component="li" sx={{ '& > img': { mr: 2, flexShrink: 0 } }} {...props} key={option.accountId}>
                    <img
                      loading="lazy"
                      width="20"
                      src={option.avatarUrls['16x16']}
                      alt={option.displayName}
                    />
                    {option.displayName}
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Assignee"
                    name="person"
                    value={formik.values.person}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    inputProps={{
                      ...params.inputProps,
                      autoComplete: 'select-assignee',
                    }}
                    error={formik.touched.person && Boolean(formik.errors.person)}
                    helperText={formik.touched.person && formik.errors.person}
                  />
                )}
              />
              {isSubTask ? (
                <Autocomplete
                  options={TASK_CATEGORIES}
                  value={formik.values.taskName || null}
                  onChange={(_, newValue) => {
                    formik.setFieldValue("taskName", `${newValue}${
                        parentData?.issue ? " - " + parentData?.issue : ""
                      }`);
                  }}
                  onInputChange={(_, newInputValue) => {
                    formik.setFieldValue("taskName", newInputValue);
                  }}
                  onBlur={formik.handleBlur}
                  freeSolo
                  fullWidth
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Task Name"
                      name="taskName"
                      error={
                        formik.touched.taskName &&
                        Boolean(formik.errors.taskName)
                      }
                      helperText={
                        formik.touched.taskName && formik.errors.taskName
                      }
                      style={{ marginTop: "10px", marginBottom: "10px" }}
                      onBlur={formik.handleBlur}
                    />
                  )}
                />
              ) : (
                <TextField
                  label="Task Name"
                  type="text"
                  name="taskName"
                  value={formik.values.taskName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.taskName && Boolean(formik.errors.taskName)
                  }
                  helperText={formik.touched.taskName && formik.errors.taskName}
                  fullWidth
                  margin="normal"
                  style={{ marginTop: "10px", marginBottom: "10px" }}
                />
              )}
              <Box sx={{
                pb: 0,
                mb: 1.3,
                '& .tox-tinymce': {
                  border: formik.touched.description && formik.errors.description ? '1px solid #d32f2f !important' : undefined,
                  borderRadius: '4px'
                },
                '& > *': {
                  pb: '0 !important',
                  mb: '0 !important'
                }
              }}>
                <TinyMCEEditor
                  handleOnChange={(e: any) => formik.setFieldValue('description', convertHTMLToADF(e) || "")}
                  editorRef={editorRef}
                  onBlur={() => formik.setFieldTouched('description', true)}
                />
              </Box>

              {!isSubTask &&
                (
                  blockParent ?
                    <div className='select-form-control-container'>
                      <Autocomplete
                        disabled
                        defaultValue={getDefaultEpicValue}
                        id="selected-epic"
                        options={[]}
                        renderInput={(params) => <TextField {...params} name="customfield_10009" label="Epic" />}
                      />
                    </div>
                    :
                    <div className='select-form-control-container'>
                      <Autocomplete
                        open={isEpicDownOpen}
                        onOpen={() => {
                          setIsEpicDownOpen(true);
                        }}
                        onClose={() => {
                          setIsEpicDownOpen(false);
                        }}
                        onInputChange={(_, value, reason) => handleEpicSearch(value, reason)}
                        options={epicList}
                        loading={epicLoading}
                        loadingText="Loading..."
                        isOptionEqualToValue={(option, value) => option?.key === value?.key}
                        getOptionLabel={(option) => option?.fields?.summary}
                        onChange={handleEpicChange}
                        onBlur={handleEpicBlur}
                        ListboxProps={{ style: { maxHeight: 250 } }}
                        inputValue={epicSearchValue}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            name="customfield_10009"
                            error={formik.touched.customfield_10009 && Boolean(formik.errors.customfield_10009)}
                            label="Epic"
                            variant="outlined"
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {epicLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                      />
                    </div>
                )
              }

              <div  style={{ display: 'flex', width: '100%', alignItems: 'center', gap: '10px', marginBottom: "10px" }} >
                <Autocomplete
                  sx={{ width : '80%'}}
                  onInputChange={(_, value, reason) => handleComponentSearch(value, reason)}
                  options={componentsList}
                  loading={componentLoading}
                  loadingText="Loading..."
                  isOptionEqualToValue={(option, value) => option?.id === value?.id}
                  getOptionLabel={(option) => option?.name}
                  onChange={handleComponentChange}
                  onBlur={handleComponentBlur}
                  ListboxProps={{ style: { maxHeight: 250 } }}
                  inputValue={componentSearchValue}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name="components"
                      error={formik.touched.components && Boolean(formik.errors.components)}
                      label="Components"
                      variant="outlined"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {componentLoading ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              <Tooltip
                title={newComponentDetails.isAccess ? "" : "Not permitted to use this feature"}
                PopperProps={{ disablePortal: true }}
                disableFocusListener={newComponentDetails.isAccess}
                disableHoverListener={newComponentDetails.isAccess}
                disableTouchListener={newComponentDetails.isAccess}
              >
                <span>
                  <Button
                    variant="contained"
                    disabled={!newComponentDetails.isAccess || componentCreationEnabled}
                    onClick={() => setComponentCreationEnabled((prevState) => !prevState)}
                  >
                    Add Component
                  </Button>
                </span>
              </Tooltip>
              </div>

             {componentCreationEnabled ? 
              <div style={{ display: 'flex', width: '95%', flexDirection: 'column', justifyContent: 'center', border: '1px solid grey', margin: '10px 0px', padding: '10px' }}>
                <div>
                  <Autocomplete
                    id="select-Component-lead"
                    options={componentAssigneeList}
                    loading={userLoading}
                    autoHighlight
                    defaultValue={getDefaultValue}
                    onChange={(event, value: any) => {
                      const selectedAccountId = value?.accountId || '';
                      setNewComponentDetails((prev) => ({
                        ...prev,
                        leadAccountId: selectedAccountId,
                        leadDisplayName: value?.displayName || ""
                      }));
                    }}
                    isOptionEqualToValue={(option, value) => option.accountId === value.accountId}
                    getOptionLabel={(option: any) => option.displayName}
                    renderOption={(props, option) => (
                      <Box component="li" sx={{ '& > img': { mr: 2, flexShrink: 0 } }} {...props} key={option.accountId}>
                        <img
                          loading="lazy"
                          width="20"
                          src={option.avatarUrls['16x16']}
                          alt={option.displayName}
                        />
                        {option.displayName}
                      </Box>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Component Lead"
                        name="Component Lead"
                        value={newComponentDetails.leadDisplayName}
                        onChange={(e) => setNewComponentDetails((prev) => ({...prev, leadDisplayName: e.target.value}))}
                        onBlur={(e) => setNewComponentDetails((prev) => ({...prev, leadDisplayName: e.target.value}))}
                        inputProps={{
                          ...params.inputProps,
                          autoComplete: 'select-component-lead',
                        }}
                        required
                      />
                    )}
                  />
                  <TextField
                    label="Component Name"
                    type="text"
                    name="componentName"
                    value={newComponentDetails.componentName}
                    onChange={(e) => setNewComponentDetails((prev) => ({...prev, componentName: e.target.value }))}
                    onBlur={(e) => setNewComponentDetails((prev) => ({...prev, componentName: e.target.value }))}
                    fullWidth
                    required
                    margin="normal"
                    style={{ marginTop: "10px", marginBottom: "10px" }}
                  />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Button onClick={onComponentEnableClose}>Cancel</Button>
                  <Button disabled={!newComponentDetails.leadAccountId || !newComponentDetails.componentName || !newComponentDetails.leadDisplayName} onClick={handleCreationComponent}>Submit</Button>
                </div>
              </div> : <></>}

              <div className='select-form-control-container'>
                <FormControl className='add-meeting-select-form-control' fullWidth >
                  <InputLabel className='bg-white' id="Team-label"> Responsible Team </InputLabel>
                  <Select
                    labelId="Team-label"
                    name="customfield_11241"
                    label="Responsible Team"
                    variant="outlined"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.customfield_11241}
                    error={formik.touched.customfield_11241 && Boolean(formik.errors.customfield_11241)}
                    fullWidth
                    MenuProps={dropDownMenuProps}
                  >
                    {
                      responsibleTeamList?.map((team: any) => (
                        <MenuItem key={team?.id} value={team?.id}>{team?.value}</MenuItem>
                      ))
                    }
                  </Select>
                </FormControl>
              </div>

              <Grid>
                <Grid item xs={12}>
                  <Typography gutterBottom>
                    Time Estimation
                  </Typography>
                </Grid>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <FormControl fullWidth>
                      <InputLabel className='bg-white' id="timeEstimation-days-label">Days</InputLabel>
                      <Select
                        labelId="timeEstimation-days-label"
                        id="timeEstimation.days"
                        name="timeEstimation.days"
                        value={formik.values.timeEstimation.days}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.timeEstimation?.days && Boolean(formik.errors.timeEstimation?.days)}
                      >
                        <MenuItem className='empty-select-item' value=""></MenuItem>
                        <MenuItem value="1">1</MenuItem>
                        <MenuItem value="2">2</MenuItem>
                        <MenuItem value="3">3</MenuItem>
                        <MenuItem value="4">4</MenuItem>
                        <MenuItem value="5">5</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={4}>
                    <FormControl fullWidth>
                      <InputLabel className='bg-white' id="timeEstimation-hours-label">Hours</InputLabel>
                      <Select
                        labelId="timeEstimation-hours-label"
                        id="timeEstimation.hours"
                        name="timeEstimation.hours"
                        value={formik.values.timeEstimation.hours}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.timeEstimation?.hours && Boolean(formik.errors.timeEstimation?.hours)}
                        MenuProps={dropDownMenuProps}
                      >
                        <MenuItem className='empty-select-item' value=""></MenuItem>
                        <MenuItem value="2">2</MenuItem>
                        <MenuItem value="3">3</MenuItem>
                        <MenuItem value="4">4</MenuItem>
                        <MenuItem value="5">5</MenuItem>
                        <MenuItem value="6">6</MenuItem>
                        <MenuItem value="7">7</MenuItem>
                        <MenuItem value="8">8</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={4}>
                    <FormControl fullWidth>
                      <InputLabel className='bg-white' id="timeEstimation-minutes-label"> Minutes </InputLabel>
                      <Select
                        labelId="timeEstimation-minutes-label"
                        id="timeEstimation.minutes"
                        name="timeEstimation.minutes"
                        value={formik.values.timeEstimation.minutes}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.timeEstimation?.minutes && Boolean(formik.errors.timeEstimation?.minutes)}
                      >
                        <MenuItem className='empty-select-item' value=""></MenuItem>
                        <MenuItem value="10">10</MenuItem>
                        <MenuItem value="20">20</MenuItem>
                        <MenuItem value="30">30</MenuItem>
                        <MenuItem value="40">40</MenuItem>
                        <MenuItem value="50">50</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Grid>

              <TextField
                label="Due Date"
                type="date"
                name="dueDate"
                value={formik.values.dueDate}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.dueDate && Boolean(formik.errors.dueDate)}
                helperText={formik.touched.dueDate && formik.errors.dueDate}
                fullWidth
                margin="normal"
                InputLabelProps={{ shrink: true }}
              />
            </div>
          </DialogContent>

          <DialogActions className="fixed-footer">
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <Button variant="contained" color="secondary" onClick={handleClose}>
                Cancel
              </Button>
              <Button type="submit" variant="contained" color="primary">
                Submit
              </Button>
            </Box>
          </DialogActions>
        </form>

        <Backdrop open={fullScreenLoading} sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
          <CircularProgress />
        </Backdrop>
      </div>
    </Dialog>
  );
};


export default CreateJiraTaskDialog;